import React, { createContext, useState, useContext, useEffect } from 'react';
import { trackAddToCart } from '../utils/metaPixel';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);

  // Load cart from localStorage on mount
  useEffect(() => {
    const savedCart = localStorage.getItem('cart');
    if (savedCart) {
      setCartItems(JSON.parse(savedCart));
    }
  }, []);

  // Save cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(cartItems));
  }, [cartItems]);

  const normalizePower = (power) => {
    if (power === 'yes' || power === 'no' || power === '' || power === undefined) return null;
    return power || null;
  };

  const makeKey = (item) => {
    const leftPower = normalizePower(item.leftEyePower || item.selectedPower) || 'none';
    const rightPower = normalizePower(item.rightEyePower || item.selectedPower) || 'none';
    return `${item.id}::L${leftPower}::R${rightPower}`;
  };

  const addToCart = (product, quantity = 1) => {
    setCartItems(prevItems => {
      const normalizedProduct = {
        ...product,
        leftEyePower: normalizePower(product.leftEyePower || product.selectedPower),
        rightEyePower: normalizePower(product.rightEyePower || product.selectedPower),
        selectedPower: normalizePower(product.selectedPower) // Keep for backward compatibility
      };
      const newItemKey = makeKey(normalizedProduct);
      const existingItem = prevItems.find(item => makeKey(item) === newItemKey);
      
      if (existingItem) {
        return prevItems.map(item =>
          makeKey(item) === newItemKey
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      }
      
      // Normalize price field from API (final_price or sell_price) to price
      const normalizedProductWithPrice = {
        ...normalizedProduct,
        price: parseFloat(product.final_price || product.sell_price || product.price || 0),
        quantity
      };
      
      // Track AddToCart event with Meta Pixel
      if (window.fbq) {
        trackAddToCart(product, quantity);
      }
      
      return [...prevItems, normalizedProductWithPrice];
    });
  };

  const removeFromCart = (productId, leftEyePower = null, rightEyePower = null, selectedPower = null) => {
    setCartItems(prevItems => prevItems.filter(item => {
      if (item.id !== productId) return true;
      // Check if powers match
      const itemLeftPower = normalizePower(item.leftEyePower || item.selectedPower);
      const itemRightPower = normalizePower(item.rightEyePower || item.selectedPower);
      const checkLeftPower = normalizePower(leftEyePower || selectedPower);
      const checkRightPower = normalizePower(rightEyePower || selectedPower);
      return !(itemLeftPower === checkLeftPower && itemRightPower === checkRightPower);
    }));
  };

  const updateQuantity = (productId, quantity, leftEyePower = null, rightEyePower = null, selectedPower = null) => {
    if (quantity <= 0) {
      removeFromCart(productId, leftEyePower, rightEyePower, selectedPower);
      return;
    }
    
    setCartItems(prevItems =>
      prevItems.map(item => {
        if (item.id !== productId) return item;
        const itemLeftPower = normalizePower(item.leftEyePower || item.selectedPower);
        const itemRightPower = normalizePower(item.rightEyePower || item.selectedPower);
        const checkLeftPower = normalizePower(leftEyePower || selectedPower);
        const checkRightPower = normalizePower(rightEyePower || selectedPower);
        
        if (itemLeftPower === checkLeftPower && itemRightPower === checkRightPower) {
          return { ...item, quantity };
        }
        return item;
      })
    );
  };

  const clearCart = () => {
    setCartItems([]);
    localStorage.removeItem('cart');
  };

  const getTotalPrice = () => {
    return cartItems.reduce((total, item) => {
      const price = parseFloat(item.price || item.final_price || item.sell_price || 0);
      return total + (price * item.quantity);
    }, 0);
  };

  const getTotalItems = () => {
    return cartItems.reduce((total, item) => total + item.quantity, 0);
  };

  const value = {
    cartItems,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    getTotalPrice,
    getTotalItems
  };

  return (
    <CartContext.Provider value={value}>
      {children}
    </CartContext.Provider>
  );
};
